import { useForm, Controller, useWatch } from 'react-hook-form';
import companyLogo from "../assets/2.png";
import { useNavigate } from 'react-router-dom';
import './CustomerForm.css';
import { useState, useRef, useEffect, useCallback } from 'react';
import { drawTypedSignature, clearCanvasAndChange, useWarmup } from '../utils/helpers';
import { useIdleTimer } from '../utils/useIdleTimer';
import { formSchemas } from '../formSchemas';
import ProgressBar from './ProgressBar';
import axios from 'axios';

const submissionRoute = process.env.REACT_APP_CUSTOMER_SUBMISSION;
const validateRoute = process.env.REACT_APP_VALIDATE_INVITE;
const wakePing = process.env.REACT_APP_WAKE_PING;

// Idle auto-lock timing: warn at 55 min of inactivity, lock at 60 min.
const IDLE_WARN_MS = 55 * 60 * 1000;
const IDLE_LOCK_MS = 60 * 60 * 1000;
const IDLE_COUNTDOWN_SECONDS = Math.round((IDLE_LOCK_MS - IDLE_WARN_MS) / 1000);

const RESET_LINK_PHONE = '(770) 946-4287';

// "Same as Billing Address" copies these billing fields into their shipping counterparts.
// Business Name has no billing equivalent, so it is always entered by hand.
const BILLING_TO_SHIPPING = {
  billingAddress: 'shippingAddress',
  billingAddress2: 'shippingAddress2',
  billingCity: 'shippingCity',
  billingState: 'shippingState',
  billingZip: 'shippingZip',
  billingContact: 'shippingContact',
  billingPhone: 'shippingPhone',
  billingExt: 'shippingExt',
  billingEmail: 'shippingEmail',
};
const BILLING_FIELDS = Object.keys(BILLING_TO_SHIPPING);
const COPIED_SHIPPING_FIELDS = new Set(Object.values(BILLING_TO_SHIPPING));

const formatCountdown = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const statusPhrases = [
  "Packaging your application securely…",
  "Converting your details into digital ink…",
  "Formatting into HTML (the secret sauce)…",
  "Attaching to a high‑priority email route…",
  "Encrypting data for a safe journey…",
  "Sending it across the internet superhighway…",
  "Routing through secure digital checkpoints…",
  "Convincing spam filters it’s legit…",
  "Reassembling your info on the other side…",
  "Politely asking the PDF generator to cooperate…",
  "Sprinkling in some pixel-perfect alignment…",
  "Checking for typos (twice, just in case)…",
  "Sending bits to the backend and prayers to the cloud…",
  "Consulting the office printer gods…",
  "Telling your signature it looks fantastic…",
  "Waiting on a tiny green light somewhere in Montana…",
  "Retrieving data from the other side of the firewall…",
  "Synchronizing with the accounting dimension…",
  "Triple‑checking it made the jump to hyperspace…",
  "Almost there — preparing confirmation screen…"
];


export default function CustomerForm({ schemaSections, initialValues, onSubmit, applicationType, applicantId, token }) {
  const {
    register,
    handleModalSubmit: rhfHandleSubmit,
    watch,
    control,
    getValues,
    setValue,
    formState: { errors }
  } = useForm({ defaultValues: initialValues });

  const [showPreview, setShowPreview] = useState(false);
  const [toast, setToast] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [errorModal, setErrorModal] = useState({ open: false, message: "" });
  const [expiredModal, setExpiredModal] = useState({ open: false, message: "" });
  const [retryCount, setRetryCount] = useState(0);
  const [statusIndex, setStatusIndex] = useState(0);
  const [lastWarmedAt, setLastWarmedAt] = useState(0);
  const [isWarmingUp, setIsWarmingUp] = useState(false);

  // Idle auto-lock state
  const [idleWarnOpen, setIdleWarnOpen] = useState(false);
  const [idleCountdown, setIdleCountdown] = useState(IDLE_COUNTDOWN_SECONDS);
  const [isLocked, setIsLocked] = useState(false);



  const navigate = useNavigate();

  const allValues = watch();
  const printName = useWatch({ control, name: 'printName' })
  const signatureFont = useWatch({ control, name: 'signatureFont' });
  const canvasRef = useRef();

  // "Same as Billing Address": while checked, keep shipping fields mirrored from billing
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const billingValues = useWatch({ control, name: BILLING_FIELDS });
  const billingKey = JSON.stringify(billingValues);

  useEffect(() => {
    if (!sameAsBilling) return;
    BILLING_FIELDS.forEach((billingName, i) => {
      setValue(BILLING_TO_SHIPPING[billingName], billingValues[i] ?? '');
    });
    // billingKey stands in for billingValues so a new array reference alone doesn't re-run this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameAsBilling, billingKey, setValue]);


useWarmup(`${wakePing}${submissionRoute}`); // Warm up every 10 minutes

  // --- Idle auto-lock (warn at 55 min, lock at 60 min) ---
  const handleIdleWarn = useCallback(() => {
    setIdleCountdown(IDLE_COUNTDOWN_SECONDS);
    setIdleWarnOpen(true);
  }, []);

  const handleIdleResume = useCallback(() => {
    setIdleWarnOpen(false);
    setIdleCountdown(IDLE_COUNTDOWN_SECONDS);
  }, []);

  const handleIdleLock = useCallback(() => {
    setIdleWarnOpen(false);
    setIsLocked(true);
  }, []);

  useIdleTimer({
    warnAfterMs: IDLE_WARN_MS,
    lockAfterMs: IDLE_LOCK_MS,
    onWarn: handleIdleWarn,
    onResume: handleIdleResume,
    onLock: handleIdleLock,
    // Stop watching once locked, after a successful submit, or while a submit is in flight.
    enabled: !isLocked && !submissionSuccess && !isLoading,
  });

  // Visual countdown while the "are you still there?" modal is showing.
  useEffect(() => {
    if (!idleWarnOpen) return;
    const interval = setInterval(() => {
      setIdleCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [idleWarnOpen]);


const onlyDigits = (v = "") => v.replace(/\D/g, "");
const setDigitsLen = (len) => (v) => onlyDigits(v).slice(0, len);

// Which fields should be 10-digit phones / 5-digit zips
const DIGIT_10_FIELDS = new Set([
  "mainPhone", "mainPhonePersonal", "billingPhone", "shippingPhone",
  "tradePhone", "tradePhone2", "ContactPhoneNumber", "PhoneNumber",
  "ShippingPhoneNumber"
]);

const DIGIT_5_FIELDS = new Set([
  "billingZip", "shippingZip", "Zip", "ShippingZip"
]);

// Utility to build shared input props for digit fields
const digitInputProps = (maxLen) => ({
  inputMode: "numeric",      // mobile numeric keyboard
  pattern: "\\d*",           // soft-hints for browsers
  maxLength: maxLen,
  onInput: (e) => { e.target.value = onlyDigits(e.target.value).slice(0, maxLen); }
});


  // Called when user clicks the Preview button
  const handlePreview = (e) => {
    e.preventDefault();

    const missingFields = validateForm(applicationType, allValues);
    if (missingFields.length > 0) {
      const errorList = (
        <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
          {missingFields.map(field => (
            <li key={field}>{field}</li>
          ))}
        </ul>
      );

      setShowPreview(false);

      setToast({
        type: "error",
        message: (
          <div
            className="custom-scroll"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              maxHeight: "400px",          // cap height
              overflowY: "auto",           // scroll if needed
              width: "100%",               // take full width of toast
            }}
          >
            <h2 style={{ margin: "0 0 0.5rem 0", fontWeight: "bold" }}>
              Missing Required Fields
            </h2>
            <p style={{ margin: "0 0 0.5rem 0", fontWeight: "lighter" }}>
              The following fields are required before review:
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",  // two columns
                gap: "0.5rem 2rem",              // row/col spacing
                width: "100%",
              }}
            >
              {errorList}
            </div>
          </div>
        ),
      });
      setTimeout(() => {
        setToast({ type: "", message: "" });
      }, 7000);


      return;
    } else {
      setShowPreview(true);
    }
  }

  // flatten all fields into a { [name]: label } map
  // inside your component, before return…
  const normalSections = schemaSections.filter(
    s => !s.title.startsWith("TERMS")
  );
  const termsSection = schemaSections.find(
    s => s.title.startsWith("TERMS")
  );

  const validateForm = (formType, allValues) => {
    const schema = formSchemas[formType];
    if (!schema) {
      console.warn(`No schema found for form type: ${formType}`);
      return [];
    }

    const missingOrInvalid = [];

    const checkDigits = (name, label, len) => {
      const raw = allValues[name] ?? "";
      const digits = onlyDigits(raw);
      if(digits.length !== len) missingOrInvalid.push(`${label} must be ${len} digits`);
    };

    schema.forEach(section => {
      section.fields.forEach(field => {
        const value = allValues[field.name];
        if (field.required) {
          if (
            value === undefined ||
            value === null ||
            (typeof value === "string" && value.trim() === "")
          ) {
            // Prefix shipping labels so "City", "Zip Code" etc. aren't ambiguous with billing
            missingOrInvalid.push(section.title === "Shipping Information" ? `Shipping ${field.label.replace(/^Shipping /, "")}` : field.label);
            return;
          }
        }

        if (field.maxLength && String(value ?? "").length > field.maxLength) {
          missingOrInvalid.push(`${field.label} must be ${field.maxLength} characters or fewer`);
        }

        if(DIGIT_10_FIELDS.has(field.name) && (value ?? "") !== "") {
          checkDigits(field.name, field.label, 10);
        }

        if(DIGIT_5_FIELDS.has(field.name) && (value ?? "") !== "") {
          checkDigits(field.name, field.label, 5);
        }

      });
    });

    return missingOrInvalid;
  }

  // Called when they confirm in the modal
  const handleModalSubmit = async () => {
    setShowPreview(false);
    setIsLoading(true);
    setProgress(0);

    // Layer A: re-confirm the link is still alive before doing real work. A "come back
    // later" tab may have sat open past the 7-day window, so the link/applicant could be
    // gone even though it was valid when the page first loaded.
    if (token && validateRoute) {
      try {
        await axios.post(validateRoute, { token }, { timeout: 15000 });
      } catch (err) {
        const status = err.response?.status;
        setIsLoading(false);
        setProgress(0);
        setExpiredModal({
          open: true,
          message:
            status === 401
              ? `This link has expired or has already been used. Please call ${RESET_LINK_PHONE} to have a new link sent.`
              : `We couldn't find this application — the link may have expired. Please call ${RESET_LINK_PHONE} to have a new link sent.`,
        });
        return;
      }
    }

    // Build the payload
    const payload = {
      applicantId,
      applicationType,
      commercial: {
        companyLegalName: allValues.companyLegalName,
        yearsInBusiness: allValues.yearsInBusiness,
        dbaName: allValues.dbaName,
        fedTaxId: allValues.fedTaxId,
        website: allValues.website,
        mainPhone: allValues.mainPhone,
        principleOfficer: allValues.principleOfficer,
        estMonthlyPurchases: allValues.estMonthlyPurchases,
        poRequired: allValues.poRequired,
        taxExempt: allValues.taxExempt,
        accountsPayableContact: allValues.accountsPayableContact,
        apEmail: allValues.apEmail,
      },
      personal: {
        legalFirstName: allValues.legalFirstName,
        lastName: allValues.lastName,
        ssn: allValues.ssn,
        mainPhonePersonal: allValues.mainPhonePersonal,
        emailPersonal: allValues.emailPersonal
      },
      addressInfo: {
        billingAddress: allValues.billingAddress,
        billingAddress2: allValues.billingAddress2,
        billingCity: allValues.billingCity,
        billingState: allValues.billingState,
        billingZip: allValues.billingZip,
        billingContact: allValues.billingContact,
        billingPhone: allValues.billingPhone,
        billingExt: allValues.billingExt,
        billingEmail: allValues.billingEmail
      },
      shippingInfo: {
        sameAsBilling,
        shippingBusinessName: allValues.shippingBusinessName,
        shippingAddress: allValues.shippingAddress,
        shippingAddress2: allValues.shippingAddress2,
        shippingCity: allValues.shippingCity,
        shippingState: allValues.shippingState,
        shippingZip: allValues.shippingZip,
        shippingContact: allValues.shippingContact,
        shippingPhone: allValues.shippingPhone,
        shippingExt: allValues.shippingExt,
        shippingEmail: allValues.shippingEmail
      },
      tradeReferences: {
        tradeName: allValues.tradeName,
        tradePhone: allValues.tradePhone,
        tradeAccountNumber: allValues.tradeAccountNumber,
        tradeAddress: allValues.tradeAddress,
        tradeCity: allValues.tradeCity,
        tradeState: allValues.tradeState,
        tradeZip: allValues.tradeZip,
        tradeName2: allValues.tradeName2,
        tradePhone2: allValues.tradePhone2,
        tradeAccountNumber2: allValues.tradeAccountNumber2,
        tradeAddress2: allValues.tradeAddress2,
        tradeCity2: allValues.tradeCity2,
        tradeState2: allValues.tradeState2,
        tradeZip2: allValues.tradeZip2
      },
      terms: {
        printName: allValues.printName,
        signature: allValues.signature,
        title: allValues.title,
        date: allValues.date
      },
    };

    try {
      await axios.post(submissionRoute, payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 120000

      });

      setProgress(100);
      setSubmissionSuccess(true);

    } catch (err) {
      console.error(err);

      // Layer C: the backend now returns a typed error when the link is dead. These are
      // terminal — retrying won't help — so send the customer to the expiry modal instead
      // of the generic "Try Again" loop.
      const status = err.response?.status;
      const code = err.response?.data?.code;
      if (status === 410 || status === 409 || code === 'LINK_EXPIRED' || code === 'ALREADY_SUBMITTED') {
        setIsLoading(false);
        setProgress(0);
        setExpiredModal({
          open: true,
          message:
            code === 'ALREADY_SUBMITTED' || status === 409
              ? `This application has already been submitted. If you believe this is an error, please call ${RESET_LINK_PHONE}.`
              : `This link has expired. Please call ${RESET_LINK_PHONE} to have a new link sent.`,
        });
        return;
      }

      setRetryCount(prev => prev + 1);

      let message = "Something went wrong while submitting your application.";

      if (err.response) {
        const { status, data } = err.response;

        if (status >= 500) {
          message = "Our servers are busy. Please try again in 5 minutes.";
        } else if (status >= 400) {
          // Look for a backend-provided message
          if (data?.error) {
            message = data.error; // backend should send: { error: "Your message here" }
          } else {
            message = "There was an issue with your submission. Please double-check your details.";
          }
        }
      } else if (err.code === 'ECONNABORTED') {
        message = "Submission timed out. Please check your connection and try again.";
      }


      setErrorModal({ open: true, message });
      setIsLoading(false);
      setProgress(0);
    }


    setTimeout(() => {
      setToast({ type: '', message: '' });
    }, 5000);
  };

  // FAKE CREEP PROGRESS
  useEffect(() => {
    let interval;
    const duration = 120000; // 2 minutes
    const targetProgress = 95;
    const startTime = Date.now();

    if (isLoading) {
      setProgress(0);
      interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const fraction = Math.min(elapsed / duration, 1);
        const nextProgress = Math.floor(fraction * targetProgress);
        setProgress(prev => (nextProgress > prev ? nextProgress : prev));

        // Only increment statusIndex if we're not at the final phrase
        setStatusIndex(prev => {
          if (prev >= statusPhrases.length - 1) return prev;
          return prev + 1;
        });
      }, 10000); // update every 10 seconds
    }

    return () => clearInterval(interval);
  }, [isLoading, statusPhrases.length]);


  useEffect(() => {
    if (progress === 100 && submissionSuccess) {
      const timer = setTimeout(() => {
        setIsLoading(false);
        // Try to get customer email from personal section or fallback to empty string
        const customerEmail = allValues.emailPersonal || allValues.customerEmail || '';
        navigate('/submission-success', { state: { customerEmail } });
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [progress, submissionSuccess, navigate, allValues]);

  useEffect(() => {
    if (printName && signatureFont && canvasRef.current) {
      const dataUrl = drawTypedSignature(printName, signatureFont, null, canvasRef);
      setValue("signature", dataUrl);
    }
  }, [printName, signatureFont, setValue]);



  return (
    <>

      <div className="welcome-message">
        <nav style={{ textAlign: 'center', padding: '1rem', position: 'relative' }}>
          <img src={companyLogo} alt="Company Logo" style={{ maxWidth: '280px', height: 'auto' }} />
        </nav>
        <p style={{ textAlign: 'center', padding: '0 1rem 1rem 1rem', position: 'relative' }}>
          Thanks for taking a moment to fill out your application.<br />
          When you’re ready, hit <span style={{ color: '#0022b9ff', fontWeight: 'bold' }}>PREVIEW</span> below to review everything before submitting.
        </p>
      </div>

      {/* ← Form header with applicant ID */}
      <div style={{ textAlign: 'center', padding: '0 1rem 1rem 1rem', position: 'relative' }}>
        <h1>{applicationType}</h1>
      </div>

      {/* ← Form itself */}
      <div className="div-wrapper">
        <form onSubmit={e => { e.preventDefault(); rhfHandleSubmit(onSubmit)(e); }} className="dynamic-form" noValidate>

          {schemaSections.map(section => (

            <div key={section.title} className={
              `form-section
       ${section.title.startsWith("TERMS") ? "terms-section" : ""}`
                .trim()
            }>
              <h3 className="section-title">{section.title}</h3>

              {section.title.startsWith("TERMS") ? (
                <p
                  className="section-info"
                  style={{ whiteSpace: 'pre-wrap', marginBottom: '1rem' }}
                >
                  {section.sectionInfo}
                </p>
              ) : section.title === 'Trade References' ? (
                <p
                  className="section-info"
                  style={{ whiteSpace: 'pre-wrap', marginBottom: '1rem', fontStyle: 'italic', fontSize: '0.875rem', fontWeight: 'bold' }}
                >
                  {section.sectionInfo}
                </p>
              ) : section.title === 'Shipping Information' ? (
                <p
                  className="section-info"
                  style={{
                    whiteSpace: 'pre-wrap',
                    marginBottom: '1rem',
                    fontStyle: 'italic', fontSize: '0.875rem', fontWeight: 'bold'
                  }}
                >
                  {section.sectionInfo}
                </p>
              ) : null}

              {section.title === 'Shipping Information' && (
                <label
                  htmlFor="sameAsBilling"
                  style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  <input
                    id="sameAsBilling"
                    type="checkbox"
                    checked={sameAsBilling}
                    onChange={(e) => setSameAsBilling(e.target.checked)}
                  />
                  Same as Billing Address
                </label>
              )}

              {section.fields.map((field, idx) => {
                const groupClass =
                  field.type === "signature" ? "signature-group" : "form-group";

                  const isDigits10 = DIGIT_10_FIELDS.has(field.name);
                  const isDigits5  = DIGIT_5_FIELDS.has(field.name);
                  const wantDigits = isDigits10 || isDigits5;
                  const maxLen     = isDigits10 ? 10 : isDigits5 ? 5 : undefined;

                  // Mirrored from billing while "Same as Billing Address" is checked. readOnly (not
                  // disabled) so react-hook-form still keeps the value.
                  const isMirrored = sameAsBilling && COPIED_SHIPPING_FIELDS.has(field.name);
                  const mirroredProps = isMirrored
                    ? { readOnly: true, tabIndex: -1, 'aria-readonly': true, style: { backgroundColor: '#eee', pointerEvents: 'none' } }
                    : {};

                  const registerOpts = {
                    required: field.required,
                    ...(wantDigits && {
                      setValueAs: setDigitsLen(maxLen),
                      validate: {
                        digitsOnly: (v) => /^\d*$/.test(v ?? "") || "Digits only",
                        exactLen:  (v) => (onlyDigits(v ?? "").length === maxLen) || `Enter ${maxLen} digits`,
                      }
                    })
                  };

                const fieldElement = (
                  <div key={field.name} className={groupClass}>
                    <label htmlFor={field.name} className="form-label">
                      {field.label}{field.required && <span style={{ color: 'red', fontWeight: 'bold' }}> *</span>}
                    </label>

                    {field.type === "select" ? (
                      <select id={field.name} {...register(field.name, registerOpts)} {...mirroredProps}>
                        <option value="">Select…</option>
                        {field.options.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : field.type === "signature" ? (
                      <Controller
                        name={field.name}
                        control={control}
                        rules={{ required: field.required }}
                        render={({ field: { onChange } }) => (
                          <>
                            <p className='signature-instructions'>Please print your name above, select a style, and then hit the 'Create Signature' button in order to see your signature.</p>
                            <div className='signature-controls' style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                              <button type="button" onClick={() => { const { printName, signatureFont } = getValues(); drawTypedSignature(printName, signatureFont, onChange, canvasRef) }}>
                                Create Signature
                              </button>
                              <button type="button" onClick={clearCanvasAndChange(onChange, canvasRef)}>
                                Clear Signature
                              </button>
                            </div>
                            <canvas
                              ref={canvasRef}
                              width={400}
                              height={100}
                              className="signature-canvas"
                            />
                            <p className='signature-disclaimer'>* Please note that by submitting this form, you will be providing your official signature for this application.</p>
                          </>
                        )}
                      />
                    ) : field.type === 'textarea' ? (
                      <textarea
                        id={field.name}
                        {...register(field.name, registerOpts)}
                        {...(wantDigits ? digitInputProps(maxLen) : {})}
                      />
                    ) : (
                      <input
                        id={field.name}
                        type={field.type}
                        {...register(field.name, registerOpts)}
                        {...(wantDigits ? digitInputProps(maxLen) : {})}
                        {...(field.maxLength ? { maxLength: field.maxLength, 'aria-describedby': `${field.name}-hint` } : {})}
                        {...mirroredProps}
                      />
                    )}

                    {field.hint && (
                      <small id={`${field.name}-hint`} style={{ display: 'block', marginTop: '0.25rem', fontStyle: 'italic', color: '#555' }}>
                        {field.hint}
                        {field.maxLength && ` (${String(allValues[field.name] ?? '').length}/${field.maxLength})`}
                      </small>
                    )}

                    {errors[field.name] && (
                      <span className="error">This field is required</span>
                    )}
                  </div>
                );

                // Inject subheadings for trade references
                if (section.title === "Trade References") {
                  if (field.name === "tradeName") {
                    return (
                      <div key="ref1-wrapper" style={{ gridColumn: "1 / -1" }}>
                        <h4 style={{ marginTop: "1rem" }}>Trade Reference 1</h4>
                        {fieldElement}
                      </div>
                    );
                  }
                  if (field.name === "tradeName2") {
                    return (
                      <div key="ref2-wrapper" style={{ gridColumn: "1 / -1" }}>
                        <h4 style={{ marginTop: "1rem" }}>Trade Reference 2</h4>
                        {fieldElement}
                      </div>
                    );
                  }
                }

                return fieldElement;
              })}

            </div>
          ))}

          {/* Preview button (not a submit!) */}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();

              const TEN_MIN = 10 * 60 * 1000;
              const now = Date.now();

              const handleWarmupAndPreview = async () => {
                if (!lastWarmedAt || now - lastWarmedAt > TEN_MIN) {
                  setToast({ type: 'info', message: 'Waking up application servers...' });
                  setIsWarmingUp(true);
                  setProgress(15);

                  try {
                    await axios.post(submissionRoute, { warmup: true });
                    setTimeout(() => {
                      setProgress(0);
                      setIsWarmingUp(false);
                      setLastWarmedAt(Date.now());
                      handlePreview(e); // ✅ run only after rewarm
                    }, 500);
                  } catch (err) {
                    setProgress(0);
                    setIsWarmingUp(false);
                    setToast({ type: 'error', message: 'Failed to re-warm server.' });
                  }
                } else {
                  handlePreview(e); // ✅ backend already warm
                }
              };

              handleWarmupAndPreview();
            }}
            className="modal-preview-submit"
          >
            Preview Submission
          </button>
        </form>

      </div>
      {/* ← Preview Modal */}
      {showPreview && (
        <div className="modal-backdrop">
          <div className="modal">
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <img src={companyLogo} alt="Company Logo" className="company-logo" width='100' />
            </div>
            <h1>Please Confirm Your Information</h1>
            <div className="modal-content">
              {/* 2‑column grid for all non‑terms sections */}
              <div className="preview-grid">
                {normalSections.map(section => (
                  <div key={section.title} className="preview-section">
                    <h4>{section.title}</h4>

                    {section.title === "Trade References" ? (
                      <div className="preview-section">
                        <div style={{ marginTop: "0.5rem" }}>
                          <h5>Trade Reference 1</h5>
                          <p><strong>Trade Name:</strong> {allValues.tradeName}</p>
                          <p><strong>Phone:</strong> {allValues.tradePhone}</p>
                          <p><strong>Account #:</strong> {allValues.tradeAccountNumber}</p>
                          <p><strong>Address:</strong> {allValues.tradeAddress}</p>
                          <p><strong>City:</strong> {allValues.tradeCity}</p>
                          <p><strong>State:</strong> {allValues.tradeState}</p>
                          <p><strong>Zip:</strong> {allValues.tradeZip}</p>
                        </div>

                        <div className="preview-section" style={{ marginTop: "1rem" }}>
                          <h5>Trade Reference 2</h5>
                          <p><strong>Trade Name:</strong> {allValues.tradeName2}</p>
                          <p><strong>Phone:</strong> {allValues.tradePhone2}</p>
                          <p><strong>Account #:</strong> {allValues.tradeAccountNumber2}</p>
                          <p><strong>Address:</strong> {allValues.tradeAddress2}</p>
                          <p><strong>City:</strong> {allValues.tradeCity2}</p>
                          <p><strong>State:</strong> {allValues.tradeState2}</p>
                          <p><strong>Zip:</strong> {allValues.tradeZip2}</p>
                        </div>
                      </div>
                    ) : (
                      section.fields.map(field => {
                        const val = allValues[field.name] ?? '<empty>';
                        return (
                          <p key={field.name}>
                            <strong>{field.label}:</strong> {val.toString()}
                          </p>
                        );
                      })
                    )}
                  </div>
                ))}
              </div>

              {/* Full‑width TERMS at the bottom */}
              {termsSection && (
                <div className="preview-terms">
                  <h4>{termsSection.title}</h4>
                  <p className="section-info">{termsSection.sectionInfo}</p>
                  {termsSection.fields.map(field => {
                    const val = allValues[field.name] ?? '<empty>';
                    return (
                      <p key={field.name}>
                        <strong>{field.label}:</strong>{' '}
                        {field.type === 'signature'
                          ? <img src={val} alt="Signature" style={{ maxWidth: 200 }} />
                          : val.toString()
                        }
                      </p>
                    )
                  })}
                </div>
              )}

              <div className="modal-actions">
                <button
                  onClick={() => setShowPreview(false)}
                  className="btn btn-light"
                  style={{
                    padding: '1rem 2rem',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    backgroundColor: '#2A3990',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#949494ff';
                    e.target.style.color = '#ffffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#2A3990';
                    e.target.style.border = 'none';
                    e.target.style.color = 'white';
                  }}
                >
                  Continue Editing
                </button>
                <button
                  type="submit"
                  onClick={(e) => {
                    e.preventDefault(); // prevent default button behavior
                    handleModalSubmit();
                  }}
                  className="btn btn-primary"
                  style={{
                    padding: '1rem 2rem',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    backgroundColor: '#2A3990',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#137200ff';
                    e.target.style.color = '#ffffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#2A3990';
                    e.target.style.border = 'none';
                    e.target.style.color = 'white';
                  }}
                >
                  Confirm & Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {errorModal.open && (
        <div className="modal-backdrop">
          <div className="error-modal">
            <h1>Submission Error</h1>
            <p style={{ marginBottom: "1rem" }}>{errorModal.message}</p>
            <div className="error-modal-actions">
              <button
                onClick={() => {
                  setErrorModal({ open: false, message: "" });
                  setShowPreview(true); // <-- send them back to the preview modal
                }}
                className="btn btn-primary"
                style={{
                  padding: '1rem 2rem',
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  backgroundColor: '#2A3990',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#137200ff';
                  e.target.style.color = '#ffffffff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#2A3990';
                  e.target.style.color = 'white';
                }}
              >
                Try Again
              </button>

            </div>
          </div>
        </div>
      )}

      {/* Link expired / already submitted — terminal, no retry */}
      {expiredModal.open && (
        <div className="modal-backdrop">
          <div className="error-modal">
            <h1>Link No Longer Valid</h1>
            <p style={{ marginBottom: "1.5rem" }}>{expiredModal.message}</p>
            <div className="error-modal-actions">
              <button
                onClick={() => window.location.reload()}
                className="btn btn-primary"
                style={{
                  padding: '1rem 2rem',
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  backgroundColor: '#2A3990',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Start Over
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Idle warning — "are you still there?" with countdown */}
      {idleWarnOpen && !isLocked && (
        <div className="modal-backdrop">
          <div className="error-modal">
            <h1>Are you still there?</h1>
            <p style={{ marginBottom: "0.5rem" }}>
              For your security, this application will lock from inactivity in:
            </p>
            <p
              style={{
                fontSize: '2.5rem',
                fontWeight: 'bold',
                color: '#2A3990',
                margin: '0.5rem 0 1.5rem 0',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatCountdown(idleCountdown)}
            </p>
            <div className="error-modal-actions">
              <button
                onClick={handleIdleResume}
                className="btn btn-primary"
                style={{
                  padding: '1rem 2rem',
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  backgroundColor: '#2A3990',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                I'm still here
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inactivity lock — terminal, form is frozen behind the overlay */}
      {isLocked && (
        <div className="modal-backdrop">
          <div className="error-modal">
            <h1>Session Expired</h1>
            <p style={{ marginBottom: "1.5rem" }}>
              This application was locked after a long period of inactivity to keep your
              information secure. Please reopen your link to continue. If your link has
              expired, call <strong>{RESET_LINK_PHONE}</strong> to have a new one sent.
            </p>
            <div className="error-modal-actions">
              <button
                onClick={() => window.location.reload()}
                className="btn btn-primary"
                style={{
                  padding: '1rem 2rem',
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  backgroundColor: '#2A3990',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.message && (
        <div className={`toast ${toast.type}`}>
          {toast.message}
        </div>
      )}

      {isLoading && (
        <div className='modal-backdrop'>
          <div className='process-loading-modal'>
            <ProgressBar progress={progress} text={`Processing...${progress}%`} />
            <p className="please-wait">{statusPhrases[statusIndex]}</p>
            <p style={{ color: '#b80000ff', fontWeight: 'bold', fontSize: '1rem', fontStyle: 'italic' }}>Please do not refresh your browser.</p>
          </div>
        </div>
      )}



    </>
  );

}



